import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import { EditAssetDialogResult } from '../components/shared-components/edit-asset-dialog/edit-asset-dialog.component';
import {
  LinkBackendModel,
  LinkCreateModel,
  LinkDetailsBackendModel,
  LinkModel,
} from '../models/link.model';
import { TrainingCategoryService } from './training-category.service';

@Injectable({
  providedIn: 'root',
})
export class LinkService {
  private loadingLinkSubject = new BehaviorSubject<number | null>(null);

  get loadingLink$(): Observable<number | null> {
    return this.loadingLinkSubject.asObservable();
  }

  constructor(
    private http: HttpClient,
    private courseTypeService: TrainingCategoryService
  ) {}

  createLink(
    link: LinkCreateModel,
    tagIds: number[],
    seminarIds: number[] = []
  ): Observable<LinkModel> {
    const formData = new FormData();
    formData.append('mode', 'createLink');
    formData.append('title', link.title);
    formData.append('url', link.url);
    formData.append('tag_ids', JSON.stringify(tagIds));
    formData.append('seminar_ids', JSON.stringify(seminarIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkModel;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(response => response.data),
        catchError(() => throwError(() => new Error('Error creating link')))
      );
  }

  deleteLink(id: number) {
    this.loadingLinkSubject.next(id);

    const formData = new FormData();
    formData.append('mode', 'deleteLink');
    formData.append('id_link', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(response => {
          this.loadingLinkSubject.next(null);
          return response.success;
        }),
        catchError(() => {
          this.loadingLinkSubject.next(null);
          return throwError(() => new Error('Error deleting link'));
        })
      );
  }

  detachLinkFromSeminar(id: number, seminarId: number): Observable<boolean> {
    this.loadingLinkSubject.next(id);

    const formData = new FormData();
    formData.append('mode', 'deleteLink');
    formData.append('id_link', id.toString());
    formData.append('id_seminar', seminarId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(response => {
          this.loadingLinkSubject.next(null);
          return response.success;
        }),
        catchError(() => {
          this.loadingLinkSubject.next(null);
          return throwError(() => new Error('Error detaching link'));
        })
      );
  }

  getLinksBySeminar(id: number) {
    const formData = new FormData();
    formData.append('mode', 'getLinksBySeminar');
    formData.append('id_seminar', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkBackendModel[];
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(
          response => response.data?.map(l => this.parseBackendLink(l)) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting links by seminar'))
        )
      );
  }

  getLinksByUser() {
    const formData = new FormData();
    formData.append('mode', 'getLinksByUser');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkBackendModel[];
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(
          response => response.data?.map(l => this.parseBackendLink(l)) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting links by user'))
        )
      );
  }

  reorderLinks(
    linkOrder: { id: number; position: number }[],
    seminarId: number
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'reorderSeminarLinks');
    formData.append('id_seminar', seminarId.toString());
    linkOrder.forEach((lo, idx) => {
      formData.append(`id[${idx}]`, lo.id.toString());
      formData.append(`position[${idx}]`, lo.position.toString());
    });
    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(() => undefined),
        catchError(() => throwError(() => new Error('Failed to reorder links')))
      );
  }

  renameLink(linkId: number, newTitle: string): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'renameLink');
    formData.append('id_link', linkId.toString());
    formData.append('new_title', newTitle);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(response => {
          if (!response.success) {
            throw new Error(response.message);
          }
        }),
        catchError(error =>
          throwError(() => new Error(error?.message || 'Failed to rename link'))
        )
      );
  }

  parseBackendLink(link: LinkBackendModel): LinkModel {
    return {
      id: link.id,
      title: link.title,
      url: link.url,
      tags: link.tags,
      category1:
        link.category1 &&
        this.courseTypeService.parseBackendModel(link.category1),
      category2:
        link.category2 &&
        this.courseTypeService.parseBackendModel(link.category2),
      position: link.position ?? null,
      createdAt: link.createdAt ? new Date(link.createdAt) : null,
    };
  }

  getLinksBySeminars(seminarIds: number[]): Observable<LinkModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getLinksBySeminars');
    formData.append('seminar_ids', JSON.stringify(seminarIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkBackendModel[];
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(
          response => response.data?.map(l => this.parseBackendLink(l)) ?? []
        ),
        catchError(() =>
          throwError(() => new Error('Error getting links by seminars'))
        )
      );
  }

  getAllLinksAdmin(): Observable<LinkModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllLinksAdmin');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkBackendModel[];
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(
          response => response.data?.map(l => this.parseBackendLink(l)) ?? []
        ),
        catchError(() => throwError(() => new Error('Error getting all links')))
      );
  }

  getLinkDetails(linkId: number): Observable<LinkDetailsBackendModel> {
    const formData = new FormData();
    formData.append('mode', 'getLinkDetails');
    formData.append('id_link', linkId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: LinkDetailsBackendModel;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(response => response.data),
        catchError(() =>
          throwError(() => new Error('Error getting link details'))
        )
      );
  }

  updateLinkDetails(
    linkId: number,
    payload: EditAssetDialogResult & { type: 'link' }
  ): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateLinkDetails');
    formData.append('id_link', linkId.toString());
    formData.append('title', payload.name);
    formData.append('url', payload.url);
    formData.append('tag_ids', JSON.stringify(payload.tagIds));
    formData.append('seminar_ids', JSON.stringify(payload.seminarIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(`${environment.authority_short}link.php`, formData)
      .pipe(
        map(() => undefined),
        catchError(() =>
          throwError(() => new Error('Error updating link details'))
        )
      );
  }
}
